import { z } from 'zod'
import type { Locations as LocationRecord, Tags as TagRecord, Jobs as JobRecord } from '@/types/pbSchema'

// Apple documents 200m as the minimum reliable region-monitoring radius on
// iOS; below that, didEnterRegion / didExitRegion may not fire dependably.
// We're trialling 150m to balance reliability against precision for tighter
// job sites; if missed-arrivals recur we'll raise to 200. The same value
// drives the device-side clamp in lib/geo-tracking.ts and Zod form validation.
export const MIN_GEOFENCE_RADIUS = 150 // meters

export type { LocationRecord }
export type Location = LocationRecord

export type Tag = TagRecord
export type Job = JobRecord

export type LocationRecordExpand = {
    tags?: Tag[]
    tag?: Tag | null
    job?: Job
}

export type LocationPB = LocationRecord & {
    expand?: LocationRecordExpand
}

export interface GeoJSONPoint {
    type: 'Point'
    coordinates: [number, number]
    radius: number
}

export type LocationGeometry = GeoJSONPoint

export interface LocationMutatableData {
    id: string
    name: string
    description?: string | null
    geometry: LocationGeometry
    inactive?: boolean | null
    tags?: string[]
}

const geoJSONPointSchema = z.object({
    type: z.literal('Point'),
    coordinates: z
        .tuple([z.number(), z.number()])
        .refine(
            ([lng, lat]) => lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90,
            'Invalid coordinates: longitude must be between -180 and 180, latitude between -90 and 90'
        ),
    radius: z
        .number()
        .min(MIN_GEOFENCE_RADIUS, `Radius must be at least ${MIN_GEOFENCE_RADIUS} meters`),
})

const locationGeometrySchema = geoJSONPointSchema

export const locationSchema = z
    .object({
        name: z
            .string()
            .min(1, 'Location name is required')
            .max(100, 'Location name must be 100 characters or less'),
        description: z.string().max(500, 'Description must be 500 characters or less').optional(),
        geometry: locationGeometrySchema,
        inactive: z.boolean().optional(),
        tags: z.array(z.string()).optional(),
    })

export type LocationFormData = z.input<typeof locationSchema>

export interface MapBounds {
    north: number
    south: number
    east: number
    west: number
}

export interface MapCenter {
    lat: number
    lng: number
    zoom?: number
}

export const DEFAULT_MAP_CENTER: MapCenter = {
    lat: 39.8283,
    lng: -98.5795,
    zoom: 4,
}
